import React from 'react'
import { regions, rentals } from '@homevest/utils'
import { format, parseISO } from 'date-fns'
import { Text, Flex, Button, Box } from '@chakra-ui/react'
import { ChevronDoubleLeftIcon } from '@heroicons/react/20/solid'

import {
  Application,
  ColumnKey,
  DEFAULT_SORT,
  FilterColumnKey,
  SelectColumnKey,
  SortColumnKey,
  SortState,
  TableState,
  MARKET_MAP,
  STATUS_MAP,
  SAFERENT_STATUS_MAP,
  RENTAL_PROGRAM_TYPE_MAP
} from './applications-table-utils'
import { rentalApplications } from '@homevest/utils'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { Link, Tag } from 'ui'
import ColumnHeader from './ColumnHeader'
import StatusTag from './StatusTag'
import { Order_By } from 'graphql/generated'
import { SnoozeApplicationButton } from './SnoozeApplication/SnoozeApplicationButton'

const { RENTAL_PROGRAM_TYPES } = rentals
const { RENTAL_APPLICATION_STATUSES } = rentalApplications

export type ConfigInput = TableState & {
  onSort: (state: SortState) => void
  onSearch: (column: FilterColumnKey, value: string) => void
  onSelect: (column: SelectColumnKey, value: string[]) => void
  onPeek: (value: Application) => void
}

export const getColumnConfig = ({
  sortState,
  filterState,
  selectState,
  tabState,
  methodology,
  onSort,
  onSearch,
  onSelect,
  onPeek
}: ConfigInput) => {
  const statusMap = methodology === 'saferent' ? SAFERENT_STATUS_MAP : STATUS_MAP

  return [
    {
      id: ColumnKey.address,
      renderHeader: () => (
        <ColumnHeader
          label='Property address'
          sorted={isSorted(sortState, ColumnKey.address)}
          searched={filterState[ColumnKey.address]}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.address))}
          onSearch={(value) => onSearch(ColumnKey.address, value)}
          sortable
          searchable
        />
      ),
      renderCell: (data: Application, hover: boolean) => (
        <Flex direction='row' align='center' position='relative'>
          <Link to={`/applications/${data.id}`} isExternal showIcon useRouter pr={12}>
            {data.mls_listing.display_line_1 ?? null}
          </Link>
          {hover && (
            <Button
              position='absolute'
              right={0}
              variant='outline'
              size='sm'
              px={1}
              onClick={() => onPeek(data)}
            >
              <Flex direction='row' align='center'>
                <Box h={4} w={4}>
                  <ChevronDoubleLeftIcon />
                </Box>
                <Text fontSize='xs' fontWeight={600} px={1}>
                  Peek
                </Text>
              </Flex>
            </Button>
          )}
        </Flex>
      )
    },
    {
      id: ColumnKey.market,
      renderHeader: () => (
        <ColumnHeader
          label='Market'
          sorted={isSorted(sortState, ColumnKey.market)}
          selected={selectState[ColumnKey.market]}
          selectOptions={[
            regions.REGIONS.ST_LOUIS,
            regions.REGIONS.ATLANTA,
            regions.REGIONS.INDIANAPOLIS,
            regions.REGIONS.CHARLOTTE
          ].map((key) => ({
            id: key,
            label: MARKET_MAP[key]
          }))}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.market))}
          onSelect={(value) => onSelect(ColumnKey.market, value)}
          sortable
          selectable
        />
      ),
      renderCell: (data: Application) => {
        const market = data.mls_listing.region as keyof typeof MARKET_MAP | undefined

        return mapContentToBadge(market ?? '', {
          fallbackText: 'N/A',
          formatFn: (value) => MARKET_MAP[value as keyof typeof MARKET_MAP] ?? value,
          size: 'xs'
        })
      }
    },
    {
      id: ColumnKey.fund,
      renderHeader: () => <ColumnHeader label='Fund' />,
      renderCell: (data: Application) => {
        const fund =
          data.mls_listing.opportunity?.home?.portfolio_homes[0]?.most_recent_llc_property?.llc
            ?.fund_llcs[0]?.fund.abbreviation

        return mapContentToBadge(fund ?? '', {
          fallbackText: 'N/A',
          formatFn: (value) => value,
          size: 'xs'
        })
      }
    },
    {
      id: ColumnKey.lead_group,
      renderHeader: () => (
        <ColumnHeader
          label='Lead group'
          searched={filterState[ColumnKey.lead_group]}
          onSearch={(value) => onSearch(ColumnKey.lead_group, value)}
          searchable
        />
      ),
      renderCell: (data: Application) => (
        <Flex direction='column' gap={1}>
          {data.lead_group.lead_group_users.map((lgu) => (
            <Link key={lgu.id} to={`/users/${lgu.user.id}`} isExternal showIcon useRouter>
              {`${lgu.user.first_name} ${lgu.user.last_name}`}
            </Link>
          ))}
        </Flex>
      )
    },
    {
      id: ColumnKey.rental_program_type_applied_for,
      renderHeader: () => (
        <ColumnHeader
          label='Program'
          selected={selectState[ColumnKey.rental_program_type_applied_for]}
          selectOptions={Object.values(RENTAL_PROGRAM_TYPES).map((programType) => ({
            id: programType,
            label: RENTAL_PROGRAM_TYPE_MAP[programType as keyof typeof RENTAL_PROGRAM_TYPE_MAP]
          }))}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.rental_program_type_applied_for))}
          onSelect={(value) => onSelect(ColumnKey.rental_program_type_applied_for, value)}
          sortable
          selectable
        />
      ),
      renderCell: (data: Application) => (
        <Flex direction='column' gap={1}>
          {mapContentToBadge(data.rental_program_type_applied_for, { size: 'xs' })}
        </Flex>
      )
    },
    {
      id: ColumnKey.status,
      renderHeader: () => (
        <ColumnHeader
          label='Status'
          sorted={isSorted(sortState, ColumnKey.status)}
          selected={selectState[ColumnKey.status]}
          selectOptions={Object.keys(statusMap).map((key) => ({
            id: key,
            label: statusMap[key as keyof typeof statusMap]
          }))}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.status))}
          onSelect={(value) => onSelect(ColumnKey.status, value)}
          sortable
          selectable
        />
      ),
      renderCell: (data: Application) => (
        <StatusTag status={data.status} statusMap={statusMap} compact />
      )
    },
    ...(tabState === RENTAL_APPLICATION_STATUSES.PENDING_UP_AND_UP_RENTAL_AGREEMENT_COMPLETION ||
    selectState[ColumnKey.status]?.includes(
      RENTAL_APPLICATION_STATUSES.PENDING_UP_AND_UP_RENTAL_AGREEMENT_COMPLETION
    )
      ? [
          {
            id: ColumnKey.prelease,
            renderHeader: () => <ColumnHeader label='Prelease' />,
            renderCell: (data: Application) =>
              data.mls_listing.internal_attributes[0]?.internal_status === 'removed' ? (
                <Tag
                  dot
                  bg='blue.100'
                  color='blue.800'
                  dotColor='blue.400'
                  size='sm'
                  fontSize='xs'
                >
                  Prelease
                </Tag>
              ) : null
          }
        ]
      : []),
    {
      id: ColumnKey.projected_occupancy_date,
      renderHeader: () => (
        <ColumnHeader
          label='Est. move in date'
          sorted={isSorted(sortState, ColumnKey.projected_occupancy_date)}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.projected_occupancy_date))}
          sortable
        />
      ),
      renderCell: (data: Application) => (
        <Text>
          {data.projected_occupancy_date
            ? format(parseISO(data.projected_occupancy_date), 'MMM dd, yyyy')
            : 'N/A'}
        </Text>
      )
    },
    {
      id: ColumnKey.created_at,
      renderHeader: () => (
        <ColumnHeader
          label='Creation date'
          sorted={isSorted(sortState, ColumnKey.created_at)}
          onSort={() => onSort(toggleSort(sortState, ColumnKey.created_at))}
          sortable
        />
      ),
      renderCell: (data: Application) => (
        <Text>{data.created_at ? format(parseISO(data.created_at), 'MMM dd, yyyy') : null}</Text>
      )
    },
    // Removing this to reduce cluttler until we're clearer on how to prioritize
    // {
    //   id: ColumnKey.priority_score,
    //   renderHeader: () => (
    //     <ColumnHeader
    //       label='Priority score'
    //       sorted={isSorted(sortState, ColumnKey.priority_score)}
    //       onSort={() => onSort(toggleSort(sortState, ColumnKey.priority_score))}
    //       sortable
    //     />
    //   ),
    //   renderCell: (data: Application) => <Text>{data.priority_score || 'TBD'}</Text>
    // },
    {
      id: ColumnKey.submitted_at,
      renderHeader: () => <ColumnHeader label='Submitted' />,
      renderCell: (data: Application) => {
        const leadGroupUsers = data.lead_group.lead_group_users

        return leadGroupUsers.map((lgu) => (
          <Text key={lgu.id}>
            <b>
              {lgu.user.first_name} {lgu.user.last_name}
            </b>
            :{' '}
            {lgu.rental_application_submitted_at
              ? format(parseISO(lgu.rental_application_submitted_at), 'M/d/yy p')
              : '-'}
          </Text>
        ))
      }
    },
    ...(tabState === 'escalated'
      ? [
          {
            id: ColumnKey.escalation_note,
            renderHeader: () => (
              <ColumnHeader
                label='Escalation note'
                searched={filterState[ColumnKey.escalation_note]}
                onSearch={(value) => onSearch(ColumnKey.escalation_note, value)}
                searchable
              />
            ),
            renderCell: (data: Application) => (
              <Text>{data.application_escalations[0]?.reason ?? null}</Text>
            )
          },
          {
            id: ColumnKey.escalated_by,
            renderHeader: () => (
              <ColumnHeader
                label='Escalated by'
                searched={filterState[ColumnKey.escalated_by]}
                onSearch={(value) => onSearch(ColumnKey.escalated_by, value)}
                searchable
              />
            ),
            renderCell: (data: Application) => {
              const admin = data.application_escalations[0]?.assigned_by_admin
              if (!admin) {
                return null
              }
              return (
                <Text>
                  {admin.first_name} {admin.last_name}
                </Text>
              )
            }
          },
          {
            id: ColumnKey.escalated_to,
            renderHeader: () => (
              <ColumnHeader
                label='Escalated to'
                searched={filterState[ColumnKey.escalated_to]}
                onSearch={(value) => onSearch(ColumnKey.escalated_to, value)}
                searchable
              />
            ),
            renderCell: (data: Application) => {
              const admin = data.application_escalations[0]?.assigned_to_admin
              if (!admin) {
                return null
              }
              return (
                <Text>
                  {admin.first_name} {admin.last_name}
                </Text>
              )
            }
          },
          {
            id: ColumnKey.escalation_date,
            renderHeader: () => (
              <ColumnHeader
                label='Escalation date'
                sorted={isSorted(sortState, ColumnKey.escalation_date)}
                onSort={() => onSort(toggleSort(sortState, ColumnKey.escalation_date))}
                sortable
              />
            ),
            renderCell: (data: Application) => {
              const createdAt = data.application_escalations[0]?.created_at
              if (!createdAt) {
                return null
              }
              return <Text>{createdAt ? format(parseISO(createdAt), 'MMM dd, yyyy') : null}</Text>
            }
          }
        ]
      : []),
    {
      id: ColumnKey.snooze,
      renderHeader: () => (
        <ColumnHeader
          label='Snooze'
          selected={selectState[ColumnKey.snooze]}
          selectOptions={[
            {
              id: 'snoozed',
              label: 'Show snoozed'
            },
            {
              id: 'normal',
              label: 'Hide snoozed'
            }
          ]}
          onSelect={(value) => onSelect(ColumnKey.snooze, value)}
          selectable
        />
      ),
      renderCell: (data: Application) => <SnoozeApplicationButton rentalApplicationId={data.id} />
    }
  ]
}

const isSorted = (sortState: SortState, key: SortColumnKey) =>
  sortState.column === key ? sortState.type : undefined

const toggleSort = (sortState: SortState, matcher: SortColumnKey): SortState => {
  if (sortState.column !== matcher) {
    return { column: matcher, type: Order_By.Desc }
  }
  if (sortState.column === matcher && sortState.type === Order_By.Desc) {
    return { column: matcher, type: Order_By.Asc }
  }
  return DEFAULT_SORT
}
